










































































































import Vue from "vue";

import {
  GET_ACCENT_OPTIONS,
  GET_SPEED_OPTIONS,
  VUEX_USER_ACTION_NAMES,
  VUEX_USER_NAMESPACE
} from "@tt-app/constants";
import { getLabelForOptionValue } from "@tt-app/utils";
import { FormOption } from "@joyz/app-core/models/control/form";
import { Word } from "@tt-app/types";
import { AudioAccent, AudioSpeed, UserProfile, UserProfileAudioConfig } from "@tt-app/models";

import IconAustralia from "@joyz/app-core/assets/flags/Australia.svg";
import IconMuscular from "@joyz/app-core/assets/icons/white/Muscular.svg";
import IconSpeaker from "@joyz/app-core/assets/icons/default/Speaker.svg";
import IconSpeedTortoise from "@joyz/app-core/assets/icons/misc/Tortise.svg";
import IconSpeedRabbit from "@joyz/app-core/assets/icons/misc/Rabbit.svg";
import IconUserVoice from "@joyz/app-core/assets/icons/default/UserVoice.svg";
import IconCorrect from "@joyz/app-core/assets/icons/Correct.svg";
import IconIncorrect from "@joyz/app-core/assets/icons/Incorrect.svg";
import IconUnitedStates from "@joyz/app-core/assets/flags/UnitedStates.svg";
import IconUnitedKingdom from "@joyz/app-core/assets/flags/UnitedKingdom.svg";

import AudioPlaybackButton from "@tt-app/components/control/AudioPlaybackButton.vue";
import AccentCard from "@tt-app/components/user/settings/AccentCard.vue";
import DictionarySentence from "@tt-app/components/exercise/SingleSentenceReading/DictionarySentence.vue";
import DropdownButton from "@tt-app/components/ui/button/DropdownButton.vue";
import IntonationSentence from "@tt-app/components/exercise/SingleSentenceReading/IntonationSentence.vue";
import SpeechSpeedCard from "@tt-app/components/user/settings/SpeechSpeedCard.vue";

const { UPDATE_USER_PROFILE } = VUEX_USER_ACTION_NAMES;

interface Data {
  icons: { [key: string]: string };
  modals: {
    accent: {
      open: boolean;
    };
    speed: {
      open: boolean;
    };
  };
}

interface Methods {
  closeAccentModal: () => void;
  closeSpeedModal: () => void;
  openAccentModal: () => void;
  openSpeedModal: () => void;
  playUserAudio: () => void;
  setAccent: (data: AudioAccent) => void;
  setSpeed: (data: AudioSpeed) => void;
  updateUserProfile: (newSettings: Pick<UserProfile, "audio" | "useOnlyHttp">) => void;
}

interface Computed {
  accentIcon: string;
  accentOptions: FormOption<number>[];
  audioConfig: UserProfileAudioConfig;
  exampleAudioSource: string;
  singleExampleAudio: boolean;
  speedIcon: string;
  speedOptions: FormOption<number>[];
  statusIcon: string;
  userAccent?: string;
  userProfile: UserProfile;
  userSpeed?: string;
}

interface Props {
  audioFiles:
    | string
    | {
        [key: string]: {
          [key: string]: string;
        };
      };
  audioProgress: number;
  evaluations: any;
  exerciseProgress: {
    current: number;
    total: number;
  };
  instruction: string[];
  logId: number;
  practiceMode: boolean;
  problemUuid: string;
  recording: boolean;
  sentence: string;
  exampleAudioButtonClickHandler: Function;
  status: string;
  userAudio: string;
  words: Word[];
  translation: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "SingleSentenceReadingQuestion",
  components: {
    AccentCard,
    AudioPlaybackButton,
    DictionarySentence,
    DropdownButton,
    IntonationSentence,
    SpeechSpeedCard
  },
  props: {
    audioFiles: {
      type: [String, Object],
      required: true
    },
    audioProgress: {
      type: Number,
      required: true
    },
    evaluations: {
      type: Object,
      default: undefined
    },
    exerciseProgress: {
      type: Object,
      required: true
    },
    instruction: {
      type: Array,
      required: true
    },
    logId: {
      type: Number,
      default: undefined
    },
    practiceMode: {
      type: Boolean,
      required: true
    },
    problemUuid: {
      type: String,
      required: true
    },
    recording: {
      type: Boolean,
      required: true
    },
    sentence: {
      type: String,
      required: true
    },
    exampleAudioButtonClickHandler: {
      type: Function,
      required: true
    },
    status: {
      type: String,
      default: undefined
    },
    userAudio: {
      type: String,
      default: undefined
    },
    words: {
      type: Array,
      required: true
    },
    translation: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      icons: {
        australia: IconAustralia,
        correct: IconCorrect,
        incorrect: IconIncorrect,
        muscular: IconMuscular,
        rabbit: IconSpeedRabbit,
        speaker: IconSpeaker,
        tortoise: IconSpeedTortoise,
        userVoice: IconUserVoice,
        unitedKingdom: IconUnitedKingdom,
        unitedStates: IconUnitedStates
      },
      modals: {
        accent: {
          open: false
        },
        speed: {
          open: false
        }
      }
    };
  },
  computed: {
    accentIcon() {
      const icons: { [key: string]: string } = {
        0: this.icons.unitedStates,
        1: this.icons.unitedKingdom,
        2: this.icons.australia
      };

      return icons[this.audioConfig.accent];
    },

    accentOptions() {
      return GET_ACCENT_OPTIONS(this);
    },

    audioConfig() {
      if (!this.userProfile) {
        return {
          accent: 0,
          speed: 100
        };
      }

      return this.userProfile.audio;
    },

    exampleAudioSource() {
      if (!this.audioFiles) return " ";

      if (this.singleExampleAudio) {
        return this.audioFiles;
      }

      const accents = {
        0: "US",
        1: "GB",
        2: "AU"
      };
      const accent = accents[this.audioConfig.accent];

      return (this.audioFiles as any)[accent][this.audioConfig.speed];
    },

    singleExampleAudio() {
      return typeof this.audioFiles === "string";
    },

    speedIcon() {
      return this.audioConfig.speed >= 100 ? this.icons.rabbit : this.icons.tortoise;
    },

    speedOptions() {
      return GET_SPEED_OPTIONS(this);
    },

    statusIcon() {
      if (!this.status) return "";
      return this.status === "correct" ? this.icons.correct : this.icons.incorrect;
    },

    userAccent() {
      return getLabelForOptionValue(this.accentOptions, this.audioConfig.accent);
    },

    userProfile() {
      return this.$store.state.user.profile;
    },

    userSpeed() {
      return getLabelForOptionValue(this.speedOptions, this.audioConfig.speed);
    }
  },
  methods: {
    closeAccentModal() {
      this.modals.accent.open = false;
    },

    closeSpeedModal() {
      this.modals.speed.open = false;
    },

    openAccentModal() {
      if (this.recording) return;
      this.modals.accent.open = true;
    },

    openSpeedModal() {
      if (this.recording) return;
      this.modals.speed.open = true;
    },

    playUserAudio() {
      console.log("play user audio");
    },

    async setAccent(data) {
      const newSettings = {
        audio: {
          ...this.userProfile.audio,
          accent: data
        },
        useOnlyHttp: this.userProfile.useOnlyHttp
      };
      await this.updateUserProfile(newSettings);
      this.closeAccentModal();
    },

    async setSpeed(data) {
      const newSettings = {
        audio: {
          ...this.userProfile.audio,
          speed: data
        },
        useOnlyHttp: this.userProfile.useOnlyHttp
      };
      await this.updateUserProfile(newSettings);
      this.closeSpeedModal();
    },

    async updateUserProfile(settings) {
      await this.$store.dispatch(`${VUEX_USER_NAMESPACE}/${UPDATE_USER_PROFILE}`, settings);
    }
  }
});
